/**
 * roomMapgen.ts — Procedural Organic Cluster Map Generator
 * TheBurntPeanut Simulator 9000
 *
 * Design rules (strict):
 * ─────────────────────────────────────────────────────────────────────────────
 * 1. ORGANIC CLUSTERS: Rooms are placed in varied shapes — 2×2 squares,
 *    L-shapes, T-shapes, lines, plus signs, and even solo rooms. Every room
 *    in a cluster is adjacent to at least one other room in that cluster
 *    (door-to-door, grid-snapped, perfectly flush).
 *
 * 2. MANDATORY SPACING: Between any two clusters, there is ALWAYS at least 1
 *    full room of empty space. This prevents accidental cross-cluster doorway
 *    alignment and keeps clusters visually distinct.
 *
 * 3. NO STACKING: A global occupied grid tracks every placed room. Any
 *    position already occupied is rejected — rooms can never overlap.
 *
 * 4. BOSS PORTAL: Always spawns as a solo room at the farthest point from the
 *    player spawn. Guaranteed every map.
 *
 * 5. GRID SNAPPING: All rooms snap to a ROOM_SIZE grid so doorways always
 *    align perfectly. No sub-pixel or fractional positions.
 *
 * Integration note:
 *   Pure TypeScript — no React, no canvas, no browser APIs.
 *   Main game calls generateRoomMap(seed) → RoomMap data object.
 */

import { GRAFFITI_ROOM_TILES, BOSS_PORTAL_TILE, RoomTileDef,
  getFloorTileUrls, OUTSIDE_TEXTURE_URLS } from './roomTiles';
import { BOUNDLESS_MAP } from './mapChunks';   // BETA 22.96 — boundless M1 flag
export { FLOOR_TILE_URLS, FLOOR_TILE_URLS_L2, FLOOR_TILE_URLS_L3, OUTSIDE_TEXTURE_URLS, getFloorTileUrls } from './roomTiles';
import { generateShrines, generateBossShrines, type Shrine } from './shrines';
export type { Shrine } from './shrines';
import { generateRoomMachines, generateWorldMachines, type WildCardMachine } from './wildCardMachines';
export type { WildCardMachine } from './wildCardMachines';
export type { Chest } from './chests';   // 23.12 compat re-export
import { generateBarrels, type Barrel } from './barrels';
export type { Barrel } from './barrels';
import { generateChickMagnets, type ChickMagnet } from './chickMagnet';
export type { ChickMagnet } from './chickMagnet';
import { generateExteriorGreenCurves, type ExteriorGreenCurve } from './roomCollisionGrid';
export type { ExteriorGreenCurve } from './roomCollisionGrid';

// ─── Constants ────────────────────────────────────────────────────────────────
export const WORLD_SIZE    = 10000;  // Reduced world — better performance, same density
export const ROOM_SIZE     = 768;

// Number of clusters to place. Each cluster is 1–5 rooms.
// Target: ~96 rooms total across 24 clusters (avg 4 rooms each)
export const CLUSTER_COUNT = 16;

// Minimum gap between any two clusters (in room-grid cells).
// 1 = exactly 1 empty room between cluster edges.
export const CLUSTER_GAP   = 1;

// World boundary margin
export const EDGE_BUFFER   = 600;

// Doorway geometry — wider gaps = easier for enemies to navigate out
export const GAP_WIDTH  = Math.round(ROOM_SIZE * 0.38);   // 292→288wu — wider openings
export const GAP_DEPTH  = Math.round(ROOM_SIZE * 0.125);  // ~96wu — wall thickness unchanged
export const GAP_OFFSET = Math.round((ROOM_SIZE - GAP_WIDTH) / 2); // ~240wu

export const BARRICADE_H = 48;

// ─── Cluster shape definitions ────────────────────────────────────────────────
// Each shape is a list of [dgx, dgy] offsets from the cluster origin.
// All offsets form a connected graph (each cell adjacent to at least one other).
// Shapes are weighted: higher weight = more likely to appear.

interface ClusterShape {
  name: string;
  cells: Array<[number, number]>;
  weight: number;
}

const CLUSTER_SHAPES: ClusterShape[] = [
  // ── 2×2 square (4 rooms) — compact, familiar
  { name: '2x2', cells: [[0,0],[1,0],[0,1],[1,1]], weight: 20 },

  // ── L-shapes (3 rooms) — most organic, feels hand-placed
  { name: 'L-TL', cells: [[0,0],[0,1],[1,1]], weight: 18 },
  { name: 'L-TR', cells: [[1,0],[0,1],[1,1]], weight: 18 },
  { name: 'L-BL', cells: [[0,0],[1,0],[0,1]], weight: 18 },
  { name: 'L-BR', cells: [[0,0],[1,0],[1,1]], weight: 18 },

  // ── T-shapes (4 rooms) — interesting junction feel
  { name: 'T-N',  cells: [[0,0],[1,0],[2,0],[1,1]], weight: 12 },
  { name: 'T-S',  cells: [[1,0],[0,1],[1,1],[2,1]], weight: 12 },
  { name: 'T-W',  cells: [[0,0],[0,1],[1,1],[0,2]], weight: 12 },
  { name: 'T-E',  cells: [[1,0],[0,1],[1,1],[1,2]], weight: 12 },

  // ── Horizontal line (3 rooms) — corridor feel
  { name: 'H3',   cells: [[0,0],[1,0],[2,0]], weight: 14 },
  // ── Vertical line (3 rooms)
  { name: 'V3',   cells: [[0,0],[0,1],[0,2]], weight: 14 },

  // ── Horizontal line (2 rooms) — small pair
  { name: 'H2',   cells: [[0,0],[1,0]], weight: 10 },
  // ── Vertical line (2 rooms)
  { name: 'V2',   cells: [[0,0],[0,1]], weight: 10 },

  // ── Solo room — isolated, mysterious
  { name: 'solo', cells: [[0,0]], weight: 8 },

  // ── Plus/cross (5 rooms) — rare, feels like a hub
  { name: 'plus', cells: [[1,0],[0,1],[1,1],[2,1],[1,2]], weight: 6 },

  // ── Z-shape (4 rooms) — winding
  { name: 'Z-H',  cells: [[0,0],[1,0],[1,1],[2,1]], weight: 8 },
  { name: 'Z-V',  cells: [[1,0],[0,1],[1,1],[0,2]], weight: 8 },

  // ── 2×3 rectangle (6 rooms) — large compound, rare
  { name: '2x3',  cells: [[0,0],[1,0],[0,1],[1,1],[0,2],[1,2]], weight: 4 },
];

// Build weighted picker
function pickShape(rng: ReturnType<typeof mkRng>): ClusterShape {
  const total = CLUSTER_SHAPES.reduce((s, c) => s + c.weight, 0);
  let r = rng.next() * total;
  for (const shape of CLUSTER_SHAPES) {
    r -= shape.weight;
    if (r <= 0) return shape;
  }
  return CLUSTER_SHAPES[0];
}

// ─── Types ────────────────────────────────────────────────────────────────────
export type Direction = 'north' | 'south' | 'east' | 'west';

export interface GapRect {
  x: number; y: number; w: number; h: number;
  dir: Direction;
}
export interface Barricade {
  x: number; y: number; w: number; h: number;
  dir: Direction;
}
export interface WallRect {
  x: number; y: number; w: number; h: number;
}
export interface PlacedRoom {
  id:          string;
  tileId:      string;
  displayName: string;
  cdnUrl:      string;
  category:    string;
  x:           number;
  y:           number;
  size:        number;
  isBoss:      boolean;
  clusterId:   number;
  clusterShape: string;
  gaps:        GapRect[];
  barricades:  Barricade[];
  wallRects:   WallRect[];
  // BETA 16.53 — Per Dylan: rooms that the old mega boss belly-flopped on
  // get marked crushed. Crushed rooms lose their wall collision +
  // barricades + render with the crushed-floor overlay. Set by the
  // slam-impact handler in engine.ts when a mega lands within a room.
  crushed?:    boolean;
}
export interface FloorZone {
  cellX: number; cellY: number; floorUrlIndex: number;
}
export interface RoomMap {
  seed:             number;
  level:            1|2|3;
  worldSize:        number;
  roomSize:         number;
  rooms:            PlacedRoom[];
  spawnPoint:       { x: number; y: number };
  bossRoom:         PlacedRoom;
  floorTileUrls:    string[];
  outsideTextureUrl:string;
  floorZones:       FloorZone[];
  /** GOOP Shrines scattered in open ground between clusters */
  shrines:          Shrine[];
  /** GOOP Chests — one per room + equal count in open ground */
  chests:           WildCardMachine[];
  /** Total chest count for HUD display */
  chestCount:       number;
  /** Gold GOOP chest count for HUD display */
  goldChestCount:   number;
  /** Breakable barrels scattered across the map */
  barrels:          Barrel[];
  /** Chick Magnet statues — activate to vacuum all loot orbs */
  chickMagnets:     ChickMagnet[];
  /** Exterior green guide curves — convex corners, straight walls, concave pockets,
   *  and diagonal bridge curves. Generated at map init from room neighbor topology.
   *  Entities inside these curves get redirected toward the nearest room doorway. */
  exteriorGreenCurves: ExteriorGreenCurve[];
}

// ─── Seeded RNG (mulberry32) ──────────────────────────────────────────────────
function mkRng(seed: number) {
  let s = seed >>> 0;
  return {
    next(): number {
      s = (s + 0x6d2b79f5) >>> 0;
      let t = Math.imul(s ^ (s >>> 15), 1 | s);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    },
    int(a: number, b: number): number { return Math.floor(this.next() * (b - a + 1)) + a; },
    pick<T>(arr: T[]): T { return arr[Math.floor(this.next() * arr.length)]; },
    shuffle<T>(arr: T[]): T[] {
      const a = [...arr];
      for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(this.next() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
      }
      return a;
    },
  };
}

// BETA 15.33 — Export CLUSTER_SHAPES so RoomLab tester can iterate them
// without duplicating the data. ClusterShape type also exported.
export type { ClusterShape };
export { CLUSTER_SHAPES };

// ─── Geometry helpers ─────────────────────────────────────────────────────────
// BETA 15.33 — Exported so the in-game Room Lab tester can render exact same
// wall/gap/barricade geometry as the live engine (no duplication risk).
export function computeGaps(rx: number, ry: number): GapRect[] {
  const s = ROOM_SIZE, gw = GAP_WIDTH, gd = GAP_DEPTH, go = GAP_OFFSET;
  return [
    { dir: 'north', x: rx + go,       y: ry,           w: gw, h: gd },
    { dir: 'south', x: rx + go,       y: ry + s - gd,  w: gw, h: gd },
    { dir: 'west',  x: rx,            y: ry + go,      w: gd, h: gw },
    { dir: 'east',  x: rx + s - gd,   y: ry + go,      w: gd, h: gw },
  ];
}

export function computeWallRects(rx: number, ry: number): WallRect[] {
  const s = ROOM_SIZE, gw = GAP_WIDTH, gd = GAP_DEPTH, go = GAP_OFFSET;
  return [
    { x: rx,           y: ry,          w: go,          h: gd },
    { x: rx+go+gw,     y: ry,          w: s-go-gw,     h: gd },
    { x: rx,           y: ry+s-gd,     w: go,          h: gd },
    { x: rx+go+gw,     y: ry+s-gd,     w: s-go-gw,     h: gd },
    { x: rx,           y: ry,          w: gd,          h: go },
    { x: rx,           y: ry+go+gw,    w: gd,          h: s-go-gw },
    { x: rx+s-gd,      y: ry,          w: gd,          h: go },
    { x: rx+s-gd,      y: ry+go+gw,    w: gd,          h: s-go-gw },
  ];
}

export function computeBarricades(rx: number, ry: number, gaps: GapRect[]): Barricade[] {
  const barricades: Barricade[] = [];
  for (const gap of gaps) {
    let blocked = false;
    if (gap.dir === 'north' && ry < EDGE_BUFFER) blocked = true;
    if (gap.dir === 'south' && ry + ROOM_SIZE > WORLD_SIZE - EDGE_BUFFER) blocked = true;
    if (gap.dir === 'west'  && rx < EDGE_BUFFER) blocked = true;
    if (gap.dir === 'east'  && rx + ROOM_SIZE > WORLD_SIZE - EDGE_BUFFER) blocked = true;
    if (blocked) {
      let bx = gap.x, by = gap.y, bw = gap.w, bh = gap.h;
      if (gap.dir === 'north') { by = gap.y + gap.h - BARRICADE_H; bh = BARRICADE_H; }
      if (gap.dir === 'south') { by = gap.y; bh = BARRICADE_H; }
      if (gap.dir === 'west')  { bx = gap.x + gap.h - BARRICADE_H; bw = BARRICADE_H; bh = gap.w; }
      if (gap.dir === 'east')  { bx = gap.x; bw = BARRICADE_H; bh = gap.w; }
      barricades.push({ x: bx, y: by, w: bw, h: bh, dir: gap.dir });
    }
  }
  return barricades;
}

function cellKey(gx: number, gy: number): string { return `${gx},${gy}`; }
function cellToWorld(gx: number, gy: number) { return { wx: gx * ROOM_SIZE, wy: gy * ROOM_SIZE }; }

function gridBounds() {
  const minCell = Math.ceil(EDGE_BUFFER / ROOM_SIZE);
  const maxCell = Math.floor((WORLD_SIZE - EDGE_BUFFER - ROOM_SIZE) / ROOM_SIZE);
  return { minCell, maxCell };
}

// ─── Cluster placement ────────────────────────────────────────────────────────
interface ClusterOrigin {
  gx: number;
  gy: number;
  shape: ClusterShape;
}

function placeClusters(rng: ReturnType<typeof mkRng>, count: number): ClusterOrigin[] {
  const origins: ClusterOrigin[] = [];
  const { minCell, maxCell } = gridBounds();

  // Track all occupied cells including their exclusion zones
  // exclusionCells: set of cells that cannot be used (occupied + 1-cell buffer)
  const exclusionCells = new Set<string>();

  function tryPlace(gx: number, gy: number, shape: ClusterShape): boolean {
    // Get all cells this shape would occupy
    const shapeCells = shape.cells.map(([dx, dy]) => ({ gx: gx + dx, gy: gy + dy }));

    // Check bounds
    for (const c of shapeCells) {
      if (c.gx < minCell || c.gx > maxCell || c.gy < minCell || c.gy > maxCell) return false;
    }

    // Check no cell is in the exclusion zone
    for (const c of shapeCells) {
      if (exclusionCells.has(cellKey(c.gx, c.gy))) return false;
    }

    // Place: mark cells and their exclusion zones
    for (const c of shapeCells) {
      // Mark the cell itself and all neighbors within CLUSTER_GAP distance
      for (let dy = -CLUSTER_GAP; dy <= CLUSTER_GAP; dy++) {
        for (let dx = -CLUSTER_GAP; dx <= CLUSTER_GAP; dx++) {
          exclusionCells.add(cellKey(c.gx + dx, c.gy + dy));
        }
      }
    }

    origins.push({ gx, gy, shape });
    return true;
  }

  let attempts = 0;
  while (origins.length < count && attempts < count * 800) {
    attempts++;
    const shape = pickShape(rng);
    // Find the bounding box of the shape to keep it within bounds
    const maxDx = Math.max(...shape.cells.map(([dx]) => dx));
    const maxDy = Math.max(...shape.cells.map(([, dy]) => dy));
    const gx = rng.int(minCell, maxCell - maxDx);
    const gy = rng.int(minCell, maxCell - maxDy);
    tryPlace(gx, gy, shape);
  }

  return origins;
}

// ─── Main generator ───────────────────────────────────────────────────────────
export function generateRoomMap(seed: number, level: 1|2|3 = 1): RoomMap {
  const rng = mkRng(seed);
  const pool = rng.shuffle([...GRAFFITI_ROOM_TILES]);
  const FLOOR_URLS = getFloorTileUrls(level);

  // BETA 22.96 — BOUNDLESS MAP M1: no clusters, no graffiti rooms, no walls.
  // The world becomes the endless chunked Dead Lot (mapChunks.ts); shrines /
  // machines / barrels still scatter across the populated zone below.
  const origins = BOUNDLESS_MAP ? [] : placeClusters(rng, CLUSTER_COUNT);

  // Global occupied set for boss portal placement
  const occupiedCells = new Set<string>();

  const rooms: PlacedRoom[] = [];
  let poolIdx = 0;

  for (let ci = 0; ci < origins.length; ci++) {
    const { gx: ox, gy: oy, shape } = origins[ci];

    for (const [dx, dy] of shape.cells) {
      const gx = ox + dx;
      const gy = oy + dy;
      const key = cellKey(gx, gy);
      if (occupiedCells.has(key)) continue;
      occupiedCells.add(key);

      const { wx, wy } = cellToWorld(gx, gy);
      const tile: RoomTileDef = pool[poolIdx++ % pool.length];
      const gaps = computeGaps(wx, wy);
      const barricades = computeBarricades(wx, wy, gaps);
      const wallRects = computeWallRects(wx, wy);

      rooms.push({
        id: `cluster${ci}_${dx}_${dy}`,
        tileId: tile.id,
        displayName: tile.displayName,
        cdnUrl: tile.cdnUrl,
        category: tile.category,
        x: wx, y: wy,
        size: ROOM_SIZE,
        isBoss: false,
        clusterId: ci,
        clusterShape: shape.name,
        gaps, barricades, wallRects,
      });
    }
    // BETA 13.29: outsideCornerTris generation removed — replaced by exteriorGreenCurves.
  }

  // BETA 13.29: cross-cluster outside-corner triangle scan removed —
  // replaced by exteriorGreenCurves + getGreenZoneRedirect.

  // Spawn point = center of first cluster's first room
  // BETA 22.96 — boundless: no rooms; spawn at the middle of the populated zone.
  const spawnRoom = rooms[0];
  const spawnPoint = spawnRoom ? {
    x: spawnRoom.x + ROOM_SIZE / 2,
    y: spawnRoom.y + ROOM_SIZE / 2,
  } : { x: WORLD_SIZE / 2, y: WORLD_SIZE / 2 };

  // ── Boss portal: random isolated cell anywhere on the map ───────────────────
  const { minCell, maxCell } = gridBounds();
  let bossGx = -1, bossGy = -1;

  // Collect all valid isolated cells (not occupied, no occupied neighbors)
  const validBossCells: { gx: number; gy: number }[] = [];
  for (let gy = minCell; gy <= maxCell; gy += 2) {
    for (let gx = minCell; gx <= maxCell; gx += 2) {
      if (occupiedCells.has(cellKey(gx, gy))) continue;
      const hasNeighbor = [
        cellKey(gx-1,gy), cellKey(gx+1,gy),
        cellKey(gx,gy-1), cellKey(gx,gy+1),
      ].some(k => occupiedCells.has(k));
      if (hasNeighbor) continue;
      validBossCells.push({ gx, gy });
    }
  }

  // Pick a random valid cell (seeded RNG so it's deterministic per seed)
  if (validBossCells.length > 0) {
    const pick = validBossCells[Math.floor(rng.next() * validBossCells.length)];
    bossGx = pick.gx;
    bossGy = pick.gy;
  }

  // Fallback: just find any unoccupied cell
  if (bossGx === -1) {
    for (let gy = minCell; gy <= maxCell && bossGx === -1; gy++) {
      for (let gx = minCell; gx <= maxCell && bossGx === -1; gx++) {
        if (!occupiedCells.has(cellKey(gx, gy))) { bossGx = gx; bossGy = gy; }
      }
    }
  }

  occupiedCells.add(cellKey(bossGx, bossGy));
  const { wx: bWx, wy: bWy } = cellToWorld(bossGx, bossGy);
  const bossGaps = computeGaps(bWx, bWy);

  const bossRoom: PlacedRoom = {
    id: 'room_boss',
    tileId: BOSS_PORTAL_TILE.id,
    displayName: BOSS_PORTAL_TILE.displayName,
    cdnUrl: BOSS_PORTAL_TILE.cdnUrl,
    category: BOSS_PORTAL_TILE.category,
    x: bWx, y: bWy,
    size: ROOM_SIZE,
    isBoss: true,
    clusterId: -1,
    clusterShape: 'solo',
    gaps: bossGaps,
    barricades: computeBarricades(bWx, bWy, bossGaps),
    wallRects: computeWallRects(bWx, bWy),
  };
  // BETA 22.96 — boundless: the boss PORTAL keeps its position (returned as
  // bossRoom below) but is NOT a rendered/collidable room anymore.
  if (!BOUNDLESS_MAP) rooms.push(bossRoom);

  // ── Floor zones ────────────────────────────────────────────────────────────
  const CELL_SIZE = 512;
  const cellsPerAxis = Math.ceil(WORLD_SIZE / CELL_SIZE);
  const floorZones: FloorZone[] = [];
  const numFloor = FLOOR_URLS.length;
  for (let cy = 0; cy < cellsPerAxis; cy++) {
    for (let cx = 0; cx < cellsPerAxis; cx++) {
      const n = Math.sin(cx * 0.7 + seed * 0.001) * Math.cos(cy * 0.7 + seed * 0.0013) +
                Math.sin(cx * 0.3 + cy * 0.4 + seed * 0.0007) * 0.5;
      const idx = Math.abs(Math.floor(((n + 1.5) / 3.0) * numFloor)) % numFloor;
      floorZones.push({ cellX: cx, cellY: cy, floorUrlIndex: idx });
    }
  }

  // Generate shrines in open ground between clusters
  const shrineRooms = rooms.map(r => ({ x: r.x, y: r.y, size: ROOM_SIZE }));
  const buffShrines  = generateShrines(seed ^ 0xC0FFEE, WORLD_SIZE, shrineRooms, spawnPoint);
  const bossShrines  = generateBossShrines(seed ^ 0xDEAD0, WORLD_SIZE, shrineRooms, spawnPoint, buffShrines);
  const shrines      = [...buffShrines, ...bossShrines];

  // Generate chests: one per room + equal count in open world
  const roomChests = generateRoomMachines(
    rooms.map(r => ({ id: r.id, x: r.x, y: r.y, size: ROOM_SIZE })),
    seed
  );
  const worldChests = generateWorldMachines(
    seed ^ 0xFACEFEED,
    WORLD_SIZE,
    rooms.map(r => ({ x: r.x, y: r.y, size: ROOM_SIZE })),
    BOUNDLESS_MAP ? 12 : rooms.length,   // 23.24: 90→30 ("should be more rare") · 23.68: 30→12 (Dylan's live review §3b S6: "way too many... feels like a wild card empire" — finding one should matter)
    // BETA 22.99 — POI separation (v6 doc 12): machines keep 280wu from every
    // shrine footprint and 320wu from the player spawn. Shrines are generated
    // FIRST, so their positions gate machine placement here.
    [
      ...shrines.map(s => ({ x: s.x, y: s.y, r: 280 })),
      { x: spawnPoint.x, y: spawnPoint.y, r: 320 },
    ],
  );
  const chests = [...roomChests, ...worldChests];
  const goldChestCount = chests.filter(c => c.type === 'gold').length;

  const barrels = generateBarrels(
    seed ^ 0xBABECAFE,
    WORLD_SIZE,
    rooms.map(r => ({ x: r.x, y: r.y, size: ROOM_SIZE })),
    spawnPoint,
  );

  const chickMagnets = generateChickMagnets(
    seed ^ 0xFEEDF00D,
    WORLD_SIZE,
    rooms.map(r => ({ x: r.x, y: r.y, size: ROOM_SIZE })),
    spawnPoint,
  );

  // BETA 13.29: pinchTris generation removed — replaced by 3-zone collision +
  // exteriorGreenCurves + getDoorwayMagnet steering.

  return {
    seed,
    level,
    worldSize: WORLD_SIZE,
    roomSize: ROOM_SIZE,
    rooms,
    spawnPoint,
    bossRoom,
    floorTileUrls: FLOOR_URLS,
    outsideTextureUrl: OUTSIDE_TEXTURE_URLS[level],
    floorZones,
    shrines,
    chests,
    chestCount: chests.length,
    goldChestCount,
    barrels,
    chickMagnets,
    exteriorGreenCurves: generateExteriorGreenCurves(rooms, ROOM_SIZE),
  };
}
